import { array, boolean, enum as enumFactory, iso, literal, strictObject, string, union } from "../../lazy-zod.js";
import { openMeldRequestContextSchema } from "../daemon/profile-identity.js";
//#region src/schemas/http/profiles.ts
const ISO_DATE_TIME_SCHEMA = iso.datetime({ offset: true });
const ADDITIONAL_HUMAN_PROFILE_CREATION_DISABLED_CODE = "profile.additional_human_profile_creation_disabled";
const ADDITIONAL_HUMAN_PROFILE_CREATION_DISABLED_MESSAGE = "Creating additional profiles for people is currently unavailable. Use your default profile, or create an Agent.";
const AGENT_PROFILE_CREATION_IDEMPOTENCY_CONFLICT_CODE = "profile.creation_idempotency_conflict";
const AGENT_PROFILE_CREATION_IDEMPOTENCY_CONFLICT_MESSAGE = "This Agent Profile creation request was already used for a different profile. Close this dialog and try again.";
const AGENT_PROFILE_CREATION_IDEMPOTENCY_KEY_MAX_LENGTH = 512;
const profileErrorStatusSchema = union([
	literal(400),
	literal(404),
	literal(409),
	literal(500)
]);
const profileErrorResponseSchema = strictObject({
	ok: literal(false),
	code: string().min(1),
	message: string().min(1)
});
const profileRpcErrorResponseSchema = profileErrorResponseSchema.extend({ status: profileErrorStatusSchema });
const additionalHumanProfileCreationDisabledErrorResponseSchema = profileErrorResponseSchema.extend({
	code: literal(ADDITIONAL_HUMAN_PROFILE_CREATION_DISABLED_CODE),
	message: literal(ADDITIONAL_HUMAN_PROFILE_CREATION_DISABLED_MESSAGE)
});
const additionalHumanProfileCreationDisabledRpcErrorResponseSchema = additionalHumanProfileCreationDisabledErrorResponseSchema.extend({ status: literal(409) });
const authProfileKindSchema = enumFactory(["human", "agent"]);
const authProfileStatusSchema = enumFactory(["active", "disabled"]);
const authProfileManagedRoleSchema = enumFactory(["openmeld_assistant", "center_agent"]);
const authProfileViewManagedRoleSchema = authProfileManagedRoleSchema.nullish().transform((value) => value ?? null);
const authProfileViewSchema = strictObject({
	openMeldProfileId: string().min(1),
	profileName: string().min(1),
	profileHandle: string().min(1),
	profileKind: authProfileKindSchema,
	isDefault: boolean(),
	status: authProfileStatusSchema,
	ownerUserId: string().min(1).nullable(),
	organizationId: string().min(1).nullable().optional(),
	ownerName: string().min(1).optional(),
	avatarUrl: string().min(1).optional(),
	source: string().min(1),
	managedRole: authProfileViewManagedRoleSchema,
	defaultProjectId: string().trim().min(1).nullable().optional(),
	createdByUserId: string().min(1).nullable(),
	creationActorProfileId: string().min(1).nullable().optional(),
	creatorProfileId: string().min(1).nullable(),
	lastUsedAt: ISO_DATE_TIME_SCHEMA.nullable(),
	createdAt: ISO_DATE_TIME_SCHEMA,
	updatedAt: ISO_DATE_TIME_SCHEMA
}).superRefine((profile, context) => {
	if (profile.defaultProjectId === void 0 || profile.profileKind === "agent" && profile.managedRole === null) return;
	context.addIssue({
		code: "custom",
		message: "Default Project is only available for ordinary Agent Profiles.",
		path: ["defaultProjectId"]
	});
});
const updateAgentProfileDefaultProjectBodySchema = strictObject({
	expectedOrganizationId: string().trim().min(1),
	defaultProjectId: string().trim().min(1).nullable()
});
const updateAgentProfileDefaultProjectRouteParamsShape = { agentProfileId: string().trim().min(1) };
const updateAgentProfileDefaultProjectRouteParamsSchema = strictObject(updateAgentProfileDefaultProjectRouteParamsShape);
const updateAgentProfileDefaultProjectRpcInputSchema = updateAgentProfileDefaultProjectBodySchema.extend({
	agentProfileId: updateAgentProfileDefaultProjectRouteParamsShape.agentProfileId,
	context: openMeldRequestContextSchema
});
const createHumanProfileBodySchema = strictObject({
	profileName: string().trim().min(1),
	creatorProfileId: string().min(1).optional()
});
const createAgentProfileBodySchema = strictObject({
	profileName: string().trim().min(1),
	creatorProfileId: string().min(1).optional(),
	idempotencyKey: string().trim().min(1).max(512).optional()
});
const ensureManagedAgentProfileBodySchema = strictObject({
	profileName: string().trim().min(1),
	managedRole: authProfileManagedRoleSchema,
	creatorProfileId: string().min(1).optional()
});
const listProfilesResponseSchema = strictObject({
	ok: literal(true),
	profiles: array(authProfileViewSchema)
});
const profileCapabilitiesResponseSchema = strictObject({
	ok: literal(true),
	additionalHumanProfileCreationEnabled: boolean()
});
const resolveProfileResponseSchema = strictObject({
	ok: literal(true),
	profile: authProfileViewSchema
});
const createHumanProfileResponseSchema = strictObject({
	ok: literal(true),
	profile: authProfileViewSchema
});
const createAgentProfileResponseSchema = strictObject({
	ok: literal(true),
	profile: authProfileViewSchema
});
const ensureManagedAgentProfileResponseSchema = strictObject({
	ok: literal(true),
	profile: authProfileViewSchema
});
const updateProfileResponseSchema = strictObject({
	ok: literal(true),
	profile: authProfileViewSchema
});
const updateAgentProfileDefaultProjectResponseSchema = updateProfileResponseSchema;
const deleteProfileResponseSchema = strictObject({
	ok: literal(true),
	profile: authProfileViewSchema
});
const createdAgentProfileActionResponseSchema = createAgentProfileResponseSchema.refine((value) => value.profile.profileKind === "agent", "Expected an Agent Profile.");
const createdHumanProfileActionResponseSchema = createHumanProfileResponseSchema.refine((value) => value.profile.profileKind === "human", "Expected a Human Profile.");
const deletedAgentProfileActionResponseSchema = deleteProfileResponseSchema.refine((value) => value.profile.profileKind === "agent", "Expected an Agent Profile.");
//#endregion
export { ADDITIONAL_HUMAN_PROFILE_CREATION_DISABLED_CODE, ADDITIONAL_HUMAN_PROFILE_CREATION_DISABLED_MESSAGE, AGENT_PROFILE_CREATION_IDEMPOTENCY_CONFLICT_CODE, AGENT_PROFILE_CREATION_IDEMPOTENCY_CONFLICT_MESSAGE, AGENT_PROFILE_CREATION_IDEMPOTENCY_KEY_MAX_LENGTH, additionalHumanProfileCreationDisabledErrorResponseSchema, additionalHumanProfileCreationDisabledRpcErrorResponseSchema, authProfileKindSchema, authProfileManagedRoleSchema, authProfileStatusSchema, authProfileViewSchema, createAgentProfileBodySchema, createAgentProfileResponseSchema, createHumanProfileBodySchema, createHumanProfileResponseSchema, createdAgentProfileActionResponseSchema, createdHumanProfileActionResponseSchema, deleteProfileResponseSchema, deletedAgentProfileActionResponseSchema, ensureManagedAgentProfileBodySchema, ensureManagedAgentProfileResponseSchema, listProfilesResponseSchema, profileCapabilitiesResponseSchema, profileErrorResponseSchema, profileErrorStatusSchema, profileRpcErrorResponseSchema, resolveProfileResponseSchema, updateAgentProfileDefaultProjectBodySchema, updateAgentProfileDefaultProjectResponseSchema, updateAgentProfileDefaultProjectRouteParamsSchema, updateAgentProfileDefaultProjectRpcInputSchema, updateProfileResponseSchema };

//# sourceMappingURL=profiles.js.map